import * as React from 'react';
import { Component } from 'react';
import styles from '../styles/wizards/EditLinkedSpecificationWizard.scss';
import autobind from 'autobind-decorator';
import {
  __,
  mapStateToProps,
  setTableParams,
  validateForm,
} from '../core/utils';
import { readTask } from '../redux/actions/table/all_tasks';
import { readWorkingSetPlan } from '../redux/actions/table/working_set_plans';
import {
  readWorkingSetSpecification,
  updateSpecificationFileCategory,
} from '../redux/actions/table/working_set_specifications';
import {
  readProduct,
  updateProductFileCategory,
} from '../redux/actions/table/products';
import { readTender } from '../redux/actions/table/tenders';
import { listFiles, readFile } from '../redux/actions/table/files';
import { updateAttachment } from '../redux/actions/attachments';
import { setAttachmentParams } from '../redux/actions/general/attachments';
import { setNotification } from '../redux/actions/general/notification';
import * as _ from 'lodash';
import AddDocumentIcon from '../assets/images/add-document-60x60.svg';
import EditAttachmentForm from '../forms/attachment_wizard/EditAttachmentForm';

@mapStateToProps((state) => ({
  store: _.get(state.general, 'attachments'),
  task_store: _.get(state.table, 'all_tasks'),
}))
class EditAttachmentWizard extends Component {
  _attachmentCategoryOptions(file_ext) {
    const categories = [
      {
        label: __('products.flyout.files.thumbnail'),
        value: 'thumbnail',
      },
      {
        label: __('products.flyout.files.product'),
        value: 'product',
      },
      {
        label: __('products.flyout.files.drawing'),
        value: 'drawing',
      },
      {
        label: __('products.flyout.files.ambient'),
        value: 'ambient',
      },
      {
        label: __('products.flyout.files.technical'),
        value: 'technical',
      },
      {
        label: __('products.flyout.files.tileable-texture-for-3d'),
        value: 'tileable',
      },
      {
        label: __('products.flyout.files.manuals'),
        value: 'manuals',
      },
      {
        label: __('products.flyout.files.datasheets'),
        value: 'datasheets',
      },
      {
        label: __('products.flyout.files.guarantee'),
        value: 'guarantee',
      },
      {
        label: __('products.flyout.files.certificates'),
        value: 'certificates',
      },
      {
        label: __('products.flyout.files.catalogs'),
        value: 'catalogs',
      },
      {
        label: __('products.flyout.files.installation'),
        value: 'installation',
      },
      {
        label: __('products.flyout.files.bim'),
        value: 'bim',
      },
      {
        label: __('products.flyout.files.3d'),
        value: '3d',
      },
      {
        label: __('products.flyout.files.cad'),
        value: 'cad',
      },
      {
        label: __('products.flyout.files.2d'),
        value: '2d',
      },
      {
        label: __('products.flyout.files.photometrical-distribution'),
        value: 'light_data',
      },
      {
        label: __('products.flyout.files.video'),
        value: 'video',
      },
      {
        label: __('products.flyout.files.audio'),
        value: 'audio',
      },
      {
        label: __('products.flyout.files.other'),
        value: 'other',
      },
    ];

    if (_.includes(['jpg', 'jpeg', 'png'], _.toLower(file_ext))) {
      return categories;
    }

    return _.filter(
      categories,
      (category) =>
        !_.includes(
          ['thumbnail', 'product', 'drawing', 'ambient', 'technical', 'tileable'],
          category.value
        )
    );
  }

  _canEditAttachmentCategory() {
    const { edit_attachment_wizard, attachment_context } = this.props.store;

    return (
      _.includes(
        ['working_set_specification', 'product'],
        _.get(attachment_context, 'type')
      ) &&
      !_.isNil(_.get(edit_attachment_wizard, 'category'))
    );
  }

  _refreshSource() {
    const { attachment_context } = this.props.store;
    const { task_store } = this.props;

    if (_.get(attachment_context, 'type') == 'files') {
      return Promise.all([
        listFiles(attachment_context.stage_id),
        readFile(
          attachment_context.stage_id,
          attachment_context.attachment_id,
        ),
      ]);
    }

    if (_.get(attachment_context, 'type') == 'meeting_items') {
      setTableParams('all_meetings', {
        forceUpdate: true,
      });

      if (_.get(attachment_context, 'task_id')) {
        readTask(attachment_context.task_id);
      }

      return Promise.resolve();
    }

    if (_.get(attachment_context, 'type') == 'working_set_plan') {
      return readWorkingSetPlan(attachment_context.plan_id);
    }

    if (_.get(attachment_context, 'type') == 'working_set_specification') {
      return readWorkingSetSpecification(
        attachment_context.specification_id
      ).then(({ response }) => {
        setTableParams('working_set_specifications', {
          flyout: response.data,
        });
      });
    }

    if (_.get(attachment_context, 'type') == 'product') {
      return readProduct(attachment_context.product_id).then(({ response }) => {
        setTableParams('products', {
          flyout: response.data,
        });
      });
    }

    if (_.get(attachment_context, 'type') == 'tender') {
      return readTender(attachment_context.tender_id);
    }

    const task_id =
      _.get(attachment_context, 'task_id') || _.get(task_store, 'flyout.id');

    if (!task_id) {
      return Promise.resolve();
    }

    return readTask(task_id).then(({ response }) => {
      setTableParams('all_tasks', {
        flyout: response.data,
      });
    });
  }

  @autobind
  _handleSubmit(formData) {
    const { edit_attachment_wizard, attachment_context } = this.props.store;
    const actions = [
      updateAttachment(
        edit_attachment_wizard.attachment_id,
        _.omit(formData, ['category'])
      ),
    ];

    if (this._canEditAttachmentCategory()) {
      const category_action =
        _.get(attachment_context, 'type') == 'product'
          ? updateProductFileCategory(attachment_context.product_id, {
              attachment_id: edit_attachment_wizard.attachment_id,
              category: formData.category,
            })
          : updateSpecificationFileCategory(attachment_context.specification_id, {
              attachment_id: edit_attachment_wizard.attachment_id,
              category: formData.category,
            });

      actions.push(
        category_action
      );
    }

    return validateForm(
      Promise.all(actions).then(() =>
        this._refreshSource().then(() => {
          setNotification(__('attachments.notification.updated'));

          setAttachmentParams({
            edit_attachment_wizard: undefined,
            attachment_context: undefined,
          });

          this.props.onClose();
        })
      )
    );
  }

  render() {
    const { edit_attachment_wizard } = this.props.store;
    const can_edit_category = this._canEditAttachmentCategory();

    return (
      <>
        <div className={styles.header}>
          <div className={styles.left}>
            <span>{__('attachments.wizard.edit.title')}</span>
            <span>{__('attachments.wizard.edit.subtitle')}</span>
          </div>
          <div className={styles.right}>
            <AddDocumentIcon />
          </div>
        </div>
        <div className={styles.body}>
          <EditAttachmentForm
            onSubmit={this._handleSubmit}
            onClose={this.props.onClose}
            initialValues={{
              title:
                edit_attachment_wizard.title || edit_attachment_wizard.name,
              description: edit_attachment_wizard.description,
              category: edit_attachment_wizard.category,
            }}
            categoryOptions={
              can_edit_category
                ? this._attachmentCategoryOptions(edit_attachment_wizard.ext)
                : undefined
            }
          />
        </div>
      </>
    );
  }
}

export default EditAttachmentWizard;
